import { getMondayOfWeek, getSundayOfWeek } from "../utils/dateUtils";

function coerceDate(value) {
  if (!value) return null;

  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? new Date(value)
        : new Date(value);

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

function coercePositiveInteger(value, fallback = 1) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(1, Math.floor(number));
}

function addWeeks(date, weeks) {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

function getWorkoutWeek(workout) {
  return coercePositiveInteger(workout?.week, 1);
}

function getBlockWeeks(blockData, workouts) {
  const totalWeeks =
    blockData?.total_weeks ?? blockData?.totalWeeks ?? blockData?.weeks;
  const maxWorkoutWeek = workouts.reduce(
    (max, workout) => Math.max(max, getWorkoutWeek(workout)),
    1,
  );

  return Math.max(coercePositiveInteger(totalWeeks, 1), maxWorkoutWeek);
}

function getCalendarStartDate(blockData) {
  return coerceDate(
    blockData?.calendar_start_date ?? blockData?.calendarStartDate,
  );
}

function getDatedWorkouts(workouts) {
  return workouts
    .map((workout) => {
      const date = coerceDate(workout?.date);
      return date ? { date, week: getWorkoutWeek(workout) } : null;
    })
    .filter(Boolean);
}

function getStartFromDatedWorkout({ date, week }) {
  return addWeeks(getMondayOfWeek(date, true), -(week - 1));
}

function getEndFromStart(startDate, totalWeeks) {
  return getSundayOfWeek(addWeeks(startDate, totalWeeks - 1), true);
}

function getEndFromDatedWorkout({ date, week }, totalWeeks) {
  const remainingWeeks = Math.max(0, totalWeeks - week);
  return getSundayOfWeek(addWeeks(date, remainingWeeks), true);
}

export function computeBlockEffectiveDateRange(blockData = {}) {
  const workouts = Array.isArray(blockData?.workouts) ? blockData.workouts : [];
  const totalWeeks = getBlockWeeks(blockData, workouts);
  const datedWorkouts = getDatedWorkouts(workouts);

  if (datedWorkouts.length > 0) {
    const startDate = new Date(
      Math.min(
        ...datedWorkouts
          .map(getStartFromDatedWorkout)
          .map((date) => date.getTime()),
      ),
    );
    const endCandidates = [
      getEndFromStart(startDate, totalWeeks),
      ...datedWorkouts.map((workout) =>
        getEndFromDatedWorkout(workout, totalWeeks),
      ),
      ...datedWorkouts.map(({ date }) => getSundayOfWeek(date, true)),
    ];
    const endDate = new Date(
      Math.max(...endCandidates.map((date) => date.getTime())),
    );

    return {
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
    };
  }

  const calendarStartDate = getCalendarStartDate(blockData);
  if (calendarStartDate) {
    const startDate = getMondayOfWeek(calendarStartDate, true);

    return {
      effectiveStartDate: startDate,
      effectiveEndDate: getEndFromStart(startDate, totalWeeks),
    };
  }

  return {
    effectiveStartDate: null,
    effectiveEndDate: null,
  };
}

export function applyBlockEffectiveDateRangeFields(blockData = {}) {
  const { effectiveStartDate, effectiveEndDate } =
    computeBlockEffectiveDateRange(blockData);

  blockData.effective_start_date = effectiveStartDate;
  blockData.effective_end_date = effectiveEndDate;
  blockData.max_created_week = computeBlockMaxCreatedWeek(blockData);

  return blockData;
}

export function computeBlockMaxCreatedWeek(blockData = {}) {
  const workouts = Array.isArray(blockData?.workouts) ? blockData.workouts : [];

  return workouts.reduce((maxWeek, workout) => {
    const week = Number(workout?.week);
    if (!Number.isFinite(week)) return maxWeek;
    return Math.max(maxWeek, Math.floor(week));
  }, 0);
}
